import { StackView } from '@play-co/timestep-core/ui';
import Spinner from 'src/game/components/shared/Spinner';
import {
  isPopupId,
  popupRegistry,
  PopupID,
} from 'src/game/components/popups/popupRegistry';
import { createPersistentEmitter } from 'src/lib/Emitter';
import { closePopup } from 'src/lib/popups/popupOpenClose';

import { getScreen } from '../../lib/utils';
import type { Popup } from '../components/popups/Popup';

export default class PopupManager {
  private rootView: StackView;

  constructor(opts: { rootView: StackView }) {
    this.rootView = opts.rootView;

    this.createPopups();

    const openPopups: {
      [K in PopupID]?: { onClose: (...args: any) => void };
    } = {};

    createPersistentEmitter((state) => state.ui.togglePopup).addListener(
      (command) => {
        if (!command) {
          return;
        }

        const { id, enabled, opts } = command;

        if (!id) {
          return; // No command...
        }
        if (!isPopupId(id)) {
          throw new Error(`Trying to toggle unknown popup: '${id}'.`);
        }

        if (enabled) {
          if (id in openPopups) {
            // Could happen if somebody dispatches togglePopup directly.
            throw new Error(`Uncaught double open popup: '${id}'.`);
          }

          const popupFactory: Popup<any, any[]> | undefined = popupRegistry[id];
          if (!popupFactory) {
            throw new Error(`No popup factory for popup: '${id}'.`);
          }

          const superview = popupFactory.SCREEN
            ? getScreen(popupFactory.SCREEN)
            : this.rootView;

          if (!superview) {
            throw new Error(
              `No superview for popup: '${id}'. (SCREEN: ${popupFactory.SCREEN})`,
            );
          }

          openPopups[id] = popupFactory({
            superview,
            openArgs: opts,
            close: (...args: any[]) => (closePopup as any)(id, ...args),
          });
        } else {
          if (!(id in openPopups)) {
            // Could happen if somebody dispatches togglePopup directly.
            throw new Error(`Uncaught double close popup: '${id}'.`);
          }

          const { onClose } = openPopups[id]!;

          // Clean up before `onClose` (potentially) crashes
          delete openPopups[id];

          onClose(...opts.args);
        }
      },
      { noInit: true },
    );
  }

  private createPopups() {
    // ===== spinner

    new Spinner({ superview: this.rootView });

    // ===== cheat popups

    if (process.env.IS_DEVELOPMENT && !process.env.IS_EDITOR) {
      void this.createCheatPopups();
    }
  }

  /**
   * Loaded dynamically so the cheat modules stay out of the production bundle
   * entirely; they do non-trivial work at module scope.
   */
  private async createCheatPopups() {
    const [{ PopupCheats }, { PopupTestPopups }, { createCheatsButton }] =
      await Promise.all([
        import('../cheats/PopupCheats.js'),
        import('../components/popups/PopupTestPopups.js'),
        import('../components/shared/ButtonCheats.js'),
      ]);

    // The cheats popup requires a special `app` argument.
    popupRegistry.popupCheats = ({ openArgs, ...creationOpts }) => {
      const popup = new PopupCheats({ ...creationOpts });
      popup.init(openArgs);
      return { onClose: () => popup.fadeOut() };
    };

    popupRegistry.popupTestPopups = ({ openArgs, ...creationOpts }) => {
      const popup = new PopupTestPopups({ ...creationOpts });
      popup.init(openArgs);
      return { onClose: () => popup.fadeOut() };
    };

    createCheatsButton({ superview: this.rootView });
  }
}
